import i18n from '../i18n';
import { formatDate as standardFormatDate } from './dateUtils';

const t = (key) => i18n.t(key);

/**
 * reportGenerator.js
 * 
 * Generates high-fidelity, clinical-grade HTML reports for patients.
 * Includes demographic data and all clinical tabs info.
 */

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  return String(value).trim();
};

const formatDate = (date) => standardFormatDate(date);

export const generateFullClinicalReport = (data) => {
  const { 
    patient = {}, 
    history = [], 
    visits = [], 
    medications = [], 
    operations = [], 
    files = [] 
  } = data;

  const patientName = `${patient.lastName ?? patient.last_name ?? ''} ${patient.firstName ?? patient.first_name ?? ''}`.trim() || t('Unknown Patient');
  const locale = i18n.language === 'el' ? 'el-GR' : 'en-US';
  const reportDate = new Date().toLocaleString(locale);

  // Styles for the clinical report
  const styles = `
    <style>
      @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
      
      body {
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        line-height: 1.4;
        color: #1a1a1a;
        padding: 40px;
        max-width: 900px;
        margin: 0 auto;
        background-color: white;
      }

      @media print {
        body { padding: 0; margin: 0; }
        .no-print { display: none; }
        .page-break { page-break-before: always; }
      }

      .report-header {
        border-bottom: 3px solid #1976d2;
        padding-bottom: 20px;
        margin-bottom: 30px;
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
      }

      .hospital-info h1 {
        margin: 0;
        color: #1976d2;
        font-size: 28px;
        font-weight: 800;
        letter-spacing: -0.5px;
      }

      .hospital-info p {
        margin: 5px 0 0;
        color: #666;
        font-weight: 600;
        font-size: 14px;
        text-transform: uppercase;
      }

      .report-meta {
        text-align: right;
        font-size: 12px;
        color: #666;
      }

      .patient-identity {
        background-color: #f8f9fa;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        padding: 20px;
        margin-bottom: 30px;
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 20px;
      }

      .id-field .label {
        font-size: 10px;
        font-weight: 800;
        color: #777;
        text-transform: uppercase;
        margin-bottom: 4px;
      }

      .id-field .value {
        font-size: 15px;
        font-weight: 600;
        color: #333;
      }

      .section-title {
        font-size: 16px;
        font-weight: 800;
        color: #1976d2;
        text-transform: uppercase;
        border-bottom: 1px solid #e0e0e0;
        padding-bottom: 8px;
        margin-top: 40px;
        margin-bottom: 20px;
        display: flex;
        align-items: center;
        gap: 10px;
      }

      .section-title::before {
        content: "";
        display: inline-block;
        width: 4px;
        height: 18px;
        background-color: #1976d2;
        border-radius: 2px;
      }

      .data-table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 20px;
      }

      .data-table th {
        text-align: left;
        background-color: #f1f3f5;
        font-size: 11px;
        font-weight: 700;
        color: #495057;
        text-transform: uppercase;
        padding: 10px;
        border-bottom: 2px solid #dee2e6;
      }

      .data-table td {
        padding: 12px 10px;
        border-bottom: 1px solid #eee;
        font-size: 13px;
        vertical-align: top;
      }

      .card-item {
        margin-bottom: 20px;
        padding: 15px;
        border-left: 3px solid #dee2e6;
        background-color: #fcfcfc;
      }

      .card-item-header {
        display: flex;
        justify-content: space-between;
        margin-bottom: 10px;
      }

      .card-item-title {
        font-weight: 700;
        color: #333;
        font-size: 15px;
      }

      .card-item-date {
        font-weight: 600;
        color: #1976d2;
        font-size: 13px;
      }

      .card-content {
        font-size: 13px;
        color: #444;
        white-space: pre-wrap;
      }

      .tag {
        display: inline-block;
        padding: 2px 8px;
        border-radius: 4px;
        font-size: 10px;
        font-weight: 800;
        text-transform: uppercase;
        background-color: #eee;
      }

      .tag-success { background-color: #e8f5e9; color: #2e7d32; }
      .tag-error { background-color: #ffebee; color: #c62828; }

      .footer {
        margin-top: 60px;
        border-top: 1px dashed #ccc;
        padding-top: 20px;
        text-align: center;
        font-size: 10px;
        color: #999;
      }

      .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
      .full-width { grid-column: span 3; }
      
      .history-page {
        page-break-before: always;
        padding-top: 20px;
      }
      
      @media (max-width: 600px) {
        .patient-identity { grid-template-columns: 1fr; }
        .grid-2 { grid-template-columns: 1fr; }
      }
    </style>
  `;

  // Content generation
  const patientHtml = `
    <div class="report-header">
      <div class="hospital-info">
        <h1>${t('PATIENT CLINICAL SUMMARY')}</h1>
        <p>${t('MySurgeon EMR - Comprehensive Health Record')}</p>
      </div>
      <div class="report-meta">
        <div>${t('Ref:')} P-${patient.id || 'N/A'}</div>
        <div>${t('Date:')} ${reportDate}</div>
      </div>
    </div>

    <div class="patient-identity">
      <div class="id-field">
        <div class="label">${t('Patient Name')}</div>
        <div class="value">${patientName}</div>
      </div>
      <div class="id-field">
        <div class="label">${t('Age / Gender')}</div>
        <div class="value">${formatValue(patient.age)} / ${formatValue(patient.gender)}</div>
      </div>
      <div class="id-field">
        <div class="label">${t('Date of Birth')}</div>
        <div class="value">${formatDate(patient.dateOfBirth ?? patient.date_birth)}</div>
      </div>
      <div class="id-field">
        <div class="label">${t('AMKA / SSN')}</div>
        <div class="value">${formatValue(patient.amka)}</div>
      </div>
      <div class="id-field">
        <div class="label">${t('Phone')}</div>
        <div class="value">${formatValue(patient.phone)}</div>
      </div>
      <div class="id-field">
        <div class="label">${t('Last Visit')}</div>
        <div class="value">${formatDate(patient.last_visit)}</div>
      </div>
      <div class="id-field full-width">
        <div class="label">${t('Chief Diagnosis')}</div>
        <div class="value">${formatValue(patient.diagnosis)}</div>
      </div>
    </div>
  `;

  let historyHtml = '';
  if (history && history.length > 0) {
    const latestHistory = history[0]; // Usually we want the most recent
    historyHtml = `
      <div class="section-title">${t('Medical History')}</div>
      <div class="grid-2">
        <div class="id-field">
          <div class="label">${t('Height / Weight')}</div>
          <div class="value">${formatValue(latestHistory.ypsos)} cm / ${formatValue(latestHistory.baros)} kg</div>
        </div>
        <div class="id-field">
          <div class="label">${t('Allergies')}</div>
          <div class="value" style="color: #c62828; font-weight: 700;">${formatValue(latestHistory.allergies)}</div>
        </div>
        <div class="id-field full-width">
          <div class="label">${t('Previous Surgeries')}</div>
          <div class="value">${formatValue(latestHistory.epembaseis)}</div>
        </div>
        <div class="id-field">
          <div class="label">${t('Cardiovascular')}</div>
          <div class="value">${formatValue(latestHistory.kardiaaggeia)}</div>
        </div>
        <div class="id-field">
          <div class="label">${t('Respiratory')}</div>
          <div class="value">${formatValue(latestHistory.anapneustiko)}</div>
        </div>
        <div class="id-field">
          <div class="label">${t('Endocrine')}</div>
          <div class="value">${formatValue(latestHistory.endokrineis)}</div>
        </div>
        <div class="id-field">
          <div class="label">${t('Neurological')}</div>
          <div class="value">${formatValue(latestHistory.neurologiko)}</div>
        </div>
        <div class="id-field full-width">
          <div class="label">${t('Personal Habits / Lifestyle')}</div>
          <div class="value">${formatValue(latestHistory.synithies)}</div>
        </div>
        <div class="id-field full-width">
          <div class="label">${t('Clinical Notes')}</div>
          <div class="value">${formatValue(latestHistory.mynotes)}</div>
        </div>
      </div>
    `;
  }

  let medsHtml = '';
  if (medications && medications.length > 0) {
    medsHtml = `
      <div class="section-title">${t('Medication List')}</div>
      <table class="data-table">
        <thead>
          <tr>
            <th>${t('Drug Name')}</th>
            <th>${t('Dosage / Frequency')}</th>
            <th>${t('Route')}</th>
            <th>${t('Timeline')}</th>
            <th>${t('Status')}</th>
          </tr>
        </thead>
        <tbody>
          ${medications.map(m => `
            <tr>
              <td style="font-weight: 700;">${m.medication_name || m.med_name || '—'}</td>
              <td>${formatValue(m.dosage)} — ${formatValue(m.frequency)}</td>
              <td>${formatValue(m.route)}</td>
              <td>${formatDate(m.start_date)} ${m.end_date ? ` ${t('to')} ${formatDate(m.end_date)}` : `(${t('Ongoing')})`}</td>
              <td><span class="tag ${m.status === 'active' ? 'tag-success' : ''}">${t(m.status || 'Active')}</span></td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `;
  }

  let visitsHtml = '';
  if (visits && visits.length > 0) {
    visitsHtml = `
      <div class="section-title">${t('Clinical Visits History')}</div>
      ${visits.map(v => `
        <div class="card-item">
          <div class="card-item-header">
            <span class="card-item-title">${v.chief_complaint || v.provlima || t('Clinical Encounter')}</span>
            <span class="card-item-date">${formatDate(v.visit_date)}</span>
          </div>
          <div class="card-content">
            <strong>${t('Findings/Diagnosis:')}</strong> ${v.diagnosis || v.eurimata || '—'}
            <br/>
            <strong>${t('Treatment Plan:')}</strong> ${v.treatment_plan || v.plano || '—'}
            ${v.notes ? `<br/><strong>${t('Notes')}:</strong> ${v.notes}` : ''}
            ${v.follow_up ? `<br/><span style="color: #1976d2; font-weight: 600;">${t('Follow-up')}: ${formatDate(v.follow_up)}</span>` : ''}
          </div>
        </div>
      `).join('')}
    `;
  }

  let opsHtml = '';
  if (operations && operations.length > 0) {
    opsHtml = `
      <div class="section-title">${t('Surgical Operations')}</div>
      <table class="data-table">
        <thead>
          <tr>
            <th>${t('Date')}</th>
            <th>${t('Procedure Type')}</th>
            <th>${t('Hospital / Surgeon')}</th>
            <th>${t('Outcome / Findings')}</th>
          </tr>
        </thead>
        <tbody>
          ${operations.map(o => `
            <tr>
              <td style="white-space: nowrap; font-weight: 600;">${formatDate(o.date)}</td>
              <td style="font-weight: 700; color: #c62828;">${o.type}</td>
              <td>${formatValue(o.hospital)}<br/><span style="font-size: 11px; color: #666;">${formatValue(o.surgeon)}</span></td>
              <td>${o.findings || o.outcome || '—'}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `;
  }

  let filesHtml = '';
  if (files && files.length > 0) {
    filesHtml = `
      <div class="section-title">${t('Diagnostic Files & Documents')}</div>
      <table class="data-table">
        <thead>
          <tr>
            <th>${t('Filename')}</th>
            <th>${t('Type')}</th>
            <th>${t('Size')}</th>
            <th>${t('Date Added')}</th>
          </tr>
        </thead>
        <tbody>
          ${files.map(f => `
            <tr>
              <td>${f.file_name || f.filename || '—'}</td>
              <td>${formatValue(f.file_type)}</td>
              <td>${f.file_size ? `${(f.file_size / 1024 / 1024).toFixed(2)} MB` : '—'}</td>
              <td>${formatDate(f.created_at)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `;
  }

  let historyFilesHtml = '';
  const historyFiles = (files || []).filter(f => String(f.description || f.notes || '').trim().toUpperCase() === 'ΙΣΤΟΡΙΚΟ');
  
  if (historyFiles.length > 0) {
    historyFilesHtml = `
      <div class="page-break"></div>
      <div class="section-title" style="color: #c62828;">${t('Detailed History Records')} (ΙΣΤΟΡΙΚΟ)</div>
      
      ${historyFiles.map(f => {
        const mime = String(f.file_type || '').toLowerCase();
        const isImage = mime.startsWith('image/');
        const isPdf = mime === 'application/pdf' || String(f.file_name || '').toLowerCase().endsWith('.pdf');
        
        // Use renderedThumb for PDFs (provided by PatientDetailPage)
        const displayUrl = f.renderedThumb || f.signedUrl || f.file_url;
        const hasVisualContent = isImage || (isPdf && f.renderedThumb);

        let content = '';
        if (hasVisualContent && displayUrl) {
          content = `
            <div class="history-page">
              <div style="font-size: 11px; color: #666; margin-bottom: 15px; font-weight: 600; text-transform: uppercase; border-bottom: 1px solid #eee; padding-bottom: 5px;">
                ${f.file_name || f.filename} — ${formatDate(f.created_at)}
              </div>
              <div style="text-align: center;">
                <img src="${displayUrl}" style="max-width: 100%; max-height: 90vh; border: 1px solid #ddd; border-radius: 4px; box-shadow: 0 4px 12px rgba(0,0,0,0.1);" />
              </div>
            </div>
          `;
        } else {
          // Fallback attachment card
          content = `
            <div class="history-page">
              <div style="margin-bottom: 30px; padding: 30px; border: 2px dashed #dee2e6; border-radius: 8px; background-color: #f8f9fa; text-align: center;">
                <div style="font-size: 18px; font-weight: 700; color: #333; margin-bottom: 10px;">
                  📄 ${f.file_name || f.filename}
                </div>
                <div style="font-size: 14px; color: #666; margin-bottom: 20px;">
                  ${t('Clinical Document (PDF/File)')} — ${formatDate(f.created_at)}
                </div>
                <div style="font-size: 12px; font-style: italic; color: #888;">
                  ${t('See original digital record for full document.')}
                </div>
              </div>
            </div>
          `;
        }
        return content;
      }).join('')}
    `;
  }

  return `
    <!DOCTYPE html>
    <html lang="${i18n.language}">
    <head>
      <meta charset="UTF-8">
      <title>${t('Clinical Report')} - ${patientName}</title>
      ${styles}
    </head>
    <body>
      ${patientHtml}
      ${historyHtml}
      ${medsHtml}
      ${visitsHtml}
      ${opsHtml}
      ${filesHtml}
      ${historyFilesHtml}
      <div class="footer">
        ${t('Confidential Clinical Documentation')} | ${t('MySurgeon EMR Intelligence')} | ${t('Page')} 1
      </div>
    </body>
    </html>
  `;
};
